import { Component } from '@theme/component';
import { CartUpdateEvent } from '@theme/events';

/**
 * Composant pour éditer la carte-message (message/expéditeur/destinataire) d'une peluche
 * configurateur-pdp depuis le panier.
 *
 * Contrairement à cart-message-editor (simple property texte via /cart/change.js), cette
 * peluche porte son message sous forme d'une image rasterisée ("Aperçu") : /cart/change.js
 * ne supporte pas les fichiers, donc modifier le message nécessite de :
 *   1. régénérer l'image (window.CardMessageSnapshot.render)
 *   2. ajouter une NOUVELLE line item peluche en multipart avec cette image (/cart/add.js)
 *   3. mettre la quantité de l'ancienne line item à 0 (/cart/change.js), en demandant le
 *      re-rendu des sections pour rafraîchir tout le panier (même mécanisme que
 *      cart-message-editor : CartUpdateEvent + data.sections)
 *
 * @typedef {object} Refs
 * @property {HTMLButtonElement} collapseToggle
 * @property {HTMLDivElement} collapsibleBody
 * @property {HTMLSpanElement} messagePreview
 * @property {HTMLButtonElement} editTrigger
 * @property {HTMLDivElement} charCounter
 * @property {HTMLSpanElement} currentCount
 * @property {HTMLDivElement} messageDisplay
 * @property {HTMLDivElement} senderDisplay
 * @property {HTMLDivElement} recipientDisplay
 * @property {HTMLDivElement} messageEditor
 * @property {HTMLTextAreaElement} messageTextarea
 * @property {HTMLInputElement} senderInput
 * @property {HTMLInputElement} recipientInput
 * @property {HTMLButtonElement} saveBtn
 *
 * @extends {Component<Refs>}
 */
class CartCardEditor extends Component {
  /** @type {boolean} */
  #isEditing = false;

  /** @type {{ text: string, sender: string, recipient: string }} */
  #original = { text: '', sender: '', recipient: '' };

  connectedCallback() {
    super.connectedCallback();

    this.#original = {
      text: this.refs.messageTextarea?.value || '',
      sender: this.refs.senderInput?.value || this.dataset.cardSender || '',
      recipient: this.refs.recipientInput?.value || this.dataset.cardRecipient || '',
    };

    this.refs.collapseToggle?.addEventListener('click', this.#onCollapseToggle);
    this.refs.editTrigger?.addEventListener('click', this.#onEditClick);
    this.refs.messageTextarea?.addEventListener('input', this.#onTextareaInput);
    this.refs.saveBtn?.addEventListener('click', this.#onSaveClick);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.refs.collapseToggle?.removeEventListener('click', this.#onCollapseToggle);
    this.refs.editTrigger?.removeEventListener('click', this.#onEditClick);
    this.refs.messageTextarea?.removeEventListener('input', this.#onTextareaInput);
    this.refs.saveBtn?.removeEventListener('click', this.#onSaveClick);
  }

  #onCollapseToggle = () => {
    if (this.hasAttribute('data-expanded')) {
      if (this.#isEditing) return;
      this.removeAttribute('data-expanded');
    } else {
      this.setAttribute('data-expanded', '');
    }
  };

  #onEditClick = () => {
    if (this.#isEditing) return;

    this.#isEditing = true;
    this.setAttribute('data-expanded', '');

    this.refs.messageEditor?.classList.remove('hidden');
    this.refs.editTrigger?.classList.add('hidden');
    this.refs.charCounter?.classList.remove('hidden');

    [this.refs.messageTextarea, this.refs.senderInput, this.refs.recipientInput].forEach((el) => {
      if (el) el.disabled = false;
    });

    this.refs.messageTextarea?.focus();
    this.refs.saveBtn?.classList.remove('hidden');
    this.#updateCharacterCount();
  };

  #onTextareaInput = () => {
    this.#updateCharacterCount();
  };

  #updateCharacterCount() {
    if (!this.refs.messageTextarea || !this.refs.currentCount) return;

    const length = this.refs.messageTextarea.value.length;
    this.refs.currentCount.textContent = length.toString();

    if (this.refs.charCounter) {
      if (length >= 380) {
        this.refs.charCounter.classList.add('warning');
        this.refs.charCounter.classList.remove('normal');
      } else if (length >= 350) {
        this.refs.charCounter.classList.add('normal');
        this.refs.charCounter.classList.remove('warning');
      } else {
        this.refs.charCounter.classList.remove('warning', 'normal');
      }
    }
  }

  #onSaveClick = async () => {
    if (!this.refs.saveBtn) return;

    const next = {
      text: (this.refs.messageTextarea?.value || '').trim(),
      sender: this.refs.senderInput ? this.refs.senderInput.value.trim() : this.#original.sender,
      recipient: this.refs.recipientInput ? this.refs.recipientInput.value.trim() : this.#original.recipient,
    };

    if (next.text === this.#original.text && next.sender === this.#original.sender && next.recipient === this.#original.recipient) {
      this.#resetToDefaultState();
      return;
    }

    this.#setLoadingState(true);
    window.SendloveCartAnalytics?.track('cart_card_save_attempt');

    try {
      await this.#regenerateCard(next);
      window.SendloveCartAnalytics?.track('cart_card_save_result', { outcome: 'saved' });

      this.#original = next;
      this.#updateDisplays(next);
      this.#resetToDefaultState();
    } catch (error) {
      window.SendloveCartAnalytics?.track('cart_card_save_result', { outcome: 'request_error' });
      console.error('[CartCardEditor] Erreur lors de la mise à jour de la carte:', error);
      alert("Une erreur est survenue lors de la modification de la carte. Veuillez réessayer.");
      this.#setLoadingState(false);
    }
  };

  /** @param {{ text: string, sender: string, recipient: string }} next */
  async #regenerateCard(next) {
    const lineKey = this.dataset.lineKey;
    const variantId = parseInt(this.dataset.variantId || '', 10);

    if (!lineKey || !variantId) {
      throw new Error('Line key ou variant id manquant');
    }

    if (!window.CardMessageSnapshot || !window.CartSubmitHelper) {
      throw new Error('CardMessageSnapshot/CartSubmitHelper indisponibles');
    }

    const cartResponse = await fetch('/cart.js');
    const cart = await cartResponse.json();
    const currentItem = cart.items.find((/** @type {{ key: string }} */ i) => i.key === lineKey);
    if (!currentItem) {
      throw new Error('Item non trouvé dans le panier');
    }

    // Carte du composer : même template (fond, police, cachet daté) que l'ajout initial.
    const template = currentItem.properties?._CardTemplate || '';
    const stampDate = currentItem.properties?._CardStampDate || '';
    const cardFile = await window.CardMessageSnapshot.render({ ...next, template, stampDate });

    // Conserver les autres choix du composer et les éventuelles propriétés de
    // ligne ; seul le fichier d'aperçu est remplacé par le nouvel upload.
    const properties = { ...currentItem.properties, _fromConfigurateur: 'true' };
    delete properties['Aperçu'];
    delete properties._CardMessage;
    delete properties._CardSender;
    delete properties._CardRecipient;
    if (template) properties._CardTemplate = template;
    if (stampDate) properties._CardStampDate = stampDate;
    if (next.text) properties._CardMessage = next.text;
    if (next.sender) properties._CardSender = next.sender;
    if (next.recipient) properties._CardRecipient = next.recipient;

    const newItem = { id: variantId, quantity: currentItem.quantity, properties };

    const cartItemsComponents = document.querySelectorAll('cart-items-component');
    const sectionsToUpdate = new Set();
    cartItemsComponents.forEach((el) => {
      if (el instanceof HTMLElement && el.dataset.sectionId) {
        sectionsToUpdate.add(el.dataset.sectionId);
      }
    });

    // Retire l'ancienne ligne AVANT d'ajouter la nouvelle : les peluches personnalisées ont
    // une quantité max de 1 par panier, donc ajouter d'abord ferait dépasser ce plafond et
    // Shopify refuserait l'ajout (422 "quantité maximale déjà dans le panier").
    const removeResponse = await fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ id: lineKey, quantity: 0 }),
    });
    if (!removeResponse.ok) throw new Error(`HTTP error! status: ${removeResponse.status}`);
    const removeData = await removeResponse.json();
    if (removeData.errors) throw new Error(removeData.errors);

    try {
      const addResponse = await window.CartSubmitHelper.postMultipart(newItem, [{ key: 'Aperçu', file: cardFile }], {
        sections: Array.from(sectionsToUpdate).join(','),
        sections_url: window.location.pathname,
      });

      if (addResponse && addResponse.sections) {
        document.dispatchEvent(
          new CartUpdateEvent({}, 'cart-card-editor', {
            itemCount: addResponse.item_count,
            source: 'cart-card-editor',
            sections: addResponse.sections,
          })
        );
      }

      return addResponse;
    } catch (addError) {
      // L'ancienne ligne a déjà été retirée mais la nouvelle n'a pas pu être ajoutée (ex:
      // coupure réseau) : on restaure la peluche telle qu'elle était (propriétés + ancien
      // "Aperçu" en texte simple, sans re-upload) plutôt que de la laisser disparaître.
      console.error('[CartCardEditor] Échec de l’ajout après suppression, restauration…', addError);
      try {
        await window.CartSubmitHelper.postJson([
          {
            id: currentItem.variant_id,
            quantity: currentItem.quantity,
            properties: currentItem.properties,
          },
        ]);
      } catch (restoreError) {
        console.error('[CartCardEditor] Restauration également en échec :', restoreError);
      }
      throw addError;
    }
  }

  /** @param {{ text: string, sender: string, recipient: string }} next */
  #updateDisplays(next) {
    if (this.refs.messagePreview) {
      this.refs.messagePreview.textContent = next.text
        ? next.text.length > 50
          ? next.text.substring(0, 47) + '...'
          : next.text
        : 'Ajouter un message';
    }
    if (this.refs.messageDisplay) {
      this.refs.messageDisplay.textContent = next.text || '';
      if (!next.text) {
        const placeholder = document.createElement('span');
        placeholder.className = 'card-editor-display__placeholder';
        placeholder.textContent = 'Aucun message';
        this.refs.messageDisplay.textContent = '';
        this.refs.messageDisplay.appendChild(placeholder);
      }
    }
    if (this.refs.senderDisplay) this.refs.senderDisplay.textContent = next.sender ? `De : ${next.sender}` : '';
    if (this.refs.recipientDisplay) this.refs.recipientDisplay.textContent = next.recipient ? `Pour : ${next.recipient}` : '';
  }

  /** @param {boolean} isLoading */
  #setLoadingState(isLoading) {
    if (!this.refs.saveBtn) return;

    const inputs = [this.refs.messageTextarea, this.refs.senderInput, this.refs.recipientInput];

    if (isLoading) {
      this.refs.saveBtn.disabled = true;
      this.refs.saveBtn.classList.add('loading');
      this.refs.saveBtn.textContent = 'Modification en cours...';
      inputs.forEach((el) => { if (el) el.disabled = true; });
    } else {
      this.refs.saveBtn.disabled = false;
      this.refs.saveBtn.classList.remove('loading');
      this.refs.saveBtn.textContent = 'Enregistrer ma carte';
      inputs.forEach((el) => { if (el) el.disabled = false; });
    }
  }

  #resetToDefaultState() {
    this.#isEditing = false;

    this.refs.messageEditor?.classList.add('hidden');
    this.refs.editTrigger?.classList.remove('hidden');
    this.refs.charCounter?.classList.add('hidden');

    [this.refs.messageTextarea, this.refs.senderInput, this.refs.recipientInput].forEach((el) => {
      if (el) el.disabled = true;
    });

    this.refs.saveBtn?.classList.add('hidden');
    this.#setLoadingState(false);
  }
}

if (!customElements.get('cart-card-editor')) {
  customElements.define('cart-card-editor', CartCardEditor);
}
